import { Directive, ElementRef, OnDestroy } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { syncHeaderFilterClearButtons } from './filter-clear-buttons';

/**
 * Makes an `<ag-grid-angular>` a `tk-grid`: the theme classes, and a funnel-with-x clear button in each header
 * cell while that column has a filter. Clicking it clears just that column's filter.
 *
 * ```html
 * <ag-grid-angular tkGrid class="w-100" style="height: 600px;" ...>
 * ```
 *
 * Sizing and column defaults come from tk-grid.ts (`tkGridOptions`, `tkColDef`); the look is in tk-grid.scss.
 */
@Directive({
  selector: 'ag-grid-angular[tkGrid]',
  standalone: true,
})
export class TkGridDirective implements OnDestroy {
  private api?: GridApi;
  private readonly subs = new Subscription();
  private initialSync?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly grid: AgGridAngular,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    // Added now, not through a host binding, so the theme is on the element before the grid is created.
    host.nativeElement.classList.add('ag-theme-alpine', 'tk-grid');

    this.subs.add(
      grid.gridReady.subscribe((event) => {
        this.api = event.api;
        // The first header render finishes after gridReady.
        this.initialSync = setTimeout(() => this.sync());
      }),
    );
    // Header cells are rebuilt as columns scroll in and out of view, so the buttons are re-added each time.
    for (const output of [
      grid.filterChanged,
      grid.virtualColumnsChanged,
      grid.firstDataRendered,
      grid.displayedColumnsChanged,
    ]) {
      this.subs.add(output.subscribe(() => this.sync()));
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.initialSync);
    this.subs.unsubscribe();
    this.api = undefined;
  }

  private sync(): void {
    const api = this.api;
    if (!api || api.isDestroyed()) {
      return;
    }
    syncHeaderFilterClearButtons(this.host.nativeElement, api.getFilterModel(), (colId) => this.clear(colId));
  }

  private clear(colId: string): void {
    const api = this.api;
    if (!colId || !api || api.isDestroyed()) {
      return;
    }
    // onFilterChanged is what makes a server-side or infinite datasource refetch.
    void api.setColumnFilterModel(colId, null).then(() => {
      if (!api.isDestroyed()) {
        api.onFilterChanged();
      }
    });
  }
}
